import { useEffect } from "react";
import { useCaptainUpIntegration } from "@/orchestrators/useCaptainUpIntegration";
import type { EngagementProvider } from "@/provider/EngagementProvider";
import type { ProviderRuntimeSnapshot } from "@/provider/ProviderTypes";
import type { ShellRuntimeState } from "@/shell/ShellRuntimeReducer";
import { isPopupVisiblePhase } from "@/shell/ShellRuntimeTypes";
import { debugLog } from "@/utils/debug";

export type UseShellOrchestratorInput = {
	state: ShellRuntimeState;

	hostTransportReady: boolean;
	hostConfigured: boolean;
	hostGameReady: boolean;
	providerBootEligible: boolean;

	provider: EngagementProvider | null;
	providerReady: boolean;
	providerRuntimeSnapshot: ProviderRuntimeSnapshot;

	activeBootGeneration: number;
	readyBootGeneration: number | null;
	inFlightBootGeneration: number | null;
};

export function useShellOrchestrator({
	state,
	hostTransportReady,
	hostConfigured,
	hostGameReady,
	providerBootEligible,
	provider,
	providerReady,
	providerRuntimeSnapshot,
	activeBootGeneration,
	readyBootGeneration,
	inFlightBootGeneration,
}: UseShellOrchestratorInput): void {
	useCaptainUpIntegration({
		provider,
		activeDockTarget: state.activeDockTarget,
		dock: state.dockState,
		popup: state.popupState,
		dockHosts: state.dockHosts,
		suppressNextProtocolCloseToken: state.suppressNextProtocolCloseToken,
	});

	useEffect(() => {
		debugLog("[EF ShellOrchestrator] shell state snapshot", {
			hostTransportReady,
			hostConfigured,
			hostGameReady,
			providerBootEligible,
			providerReady,
			hasProvider: !!provider,
			providerRuntimePhase: providerRuntimeSnapshot.phase,
			providerRuntimeProviderId: providerRuntimeSnapshot.providerId,
			providerRuntimeProcessId: providerRuntimeSnapshot.processId,
			activeBootGeneration,
			readyBootGeneration,
			inFlightBootGeneration,
			activeDockTarget: state.activeDockTarget,
			dockContentState: state.dockContentState,
			modalHasContent: state.modalHasContent,
			popupDesiredOpen: state.popupDesiredOpen,
			popupPhase: state.popupPhase,
			popupVisible: isPopupVisiblePhase(state.popupPhase),
			pendingPopupGeneration: state.pendingPopupGeneration,
			freshBootRequiredBeforeNextOpen:
				state.freshBootRequiredBeforeNextOpen,
			suppressNextProtocolCloseToken:
				state.suppressNextProtocolCloseToken,
			hasDockState: !!state.dockState,
			hasPopupState: !!state.popupState,
			popupGranted: state.popupState?.granted ?? null,
			dockHosts: {
				mission: !!state.dockHosts.mission,
				tournament: !!state.dockHosts.tournament,
			},
		});
	}, [
		hostTransportReady,
		hostConfigured,
		hostGameReady,
		providerBootEligible,
		providerReady,
		provider,
		providerRuntimeSnapshot,
		activeBootGeneration,
		readyBootGeneration,
		inFlightBootGeneration,
		state,
	]);
}

export default useShellOrchestrator;